import type { SortOrder } from '@/components/design-system/table/datatable.types';
import type { FileSearchParams, FileTableSortField } from '@/components/Views/Company/File/file-table.types';
import { FILE_TABLE_COLUMNS } from '@/components/Views/Company/File/file-table.types';
import { useFileDetailStore } from '@/stores/company/file-detail.store';
import { refDebounced } from '@vueuse/core';
import type { DataTableSortEvent } from 'primevue/datatable';
import { computed, ref, watch } from 'vue';

export const DEFAULT_ROWS_PER_PAGE = 10;
export const DEFAULT_SORT_FIELD: FileTableSortField = 'created_at';
export const DEFAULT_SORT_DIRECTION: SortOrder = 'desc';
export const SEARCH_DEBOUNCE_MS = 750;

const isSortableField = (field: DataTableSortEvent['sortField']): field is FileTableSortField =>
    FILE_TABLE_COLUMNS.some(column => column.isSortable && column.sortField === field);

export const useFileTable = () => {
    const fileDetailStore = useFileDetailStore();

    const page = ref(0);
    const rows = ref(DEFAULT_ROWS_PER_PAGE);
    const sortField = ref<FileTableSortField>(DEFAULT_SORT_FIELD);
    const sortDirection = ref<SortOrder>(DEFAULT_SORT_DIRECTION);
    const search = ref('');
    const debouncedSearch = refDebounced(search, SEARCH_DEBOUNCE_MS);

    const params = computed<FileSearchParams>(() => ({
        page: page.value + 1,
        perPage: rows.value,
        orderBy: sortField.value,
        orderDirection: sortDirection.value,
        search: debouncedSearch.value,
    }));

    const { data, error, isFetching, execute: searchFiles } = fileDetailStore.searchFiles(params);

    const files = computed(() => data.value?.data ?? []);
    const totalRecords = computed(() => data.value?.meta.total ?? 0);
    const totalRecordsUnfiltered = computed(() => data.value?.meta.total_unfiltered ?? 0);

    watch([debouncedSearch, sortField, sortDirection, rows], () => {
        page.value = 0;
    });

    watch(params, () => searchFiles(), { immediate: true, flush: 'post' });

    const handleSort = (event: DataTableSortEvent) => {
        if (!isSortableField(event.sortField)) {
            return;
        }

        sortField.value = event.sortField;
        sortDirection.value = event.sortOrder === 1 ? 'asc' : 'desc';
    };

    return {
        page,
        rows,
        search,
        sortField,
        sortDirection,
        params,
        files,
        totalRecords,
        totalRecordsUnfiltered,
        error,
        isFetching,
        searchFiles,
        handleSort,
    };
};
